/**
 * Utility for managing pending calendar event reservation intent
 * across authentication flows.
 *
 * When an unauthenticated user tries to make a reservation,
 * we store their intent here before redirecting to login.
 * After successful login, we check for and execute the pending intent.
 */

export interface IPendingReservationIntent {
  eventId: number;
  eventName: string;
  eventDate: string; // ISO date string
  startTime: string; // Format: "HH:mm"
  endTime: string; // Format: "HH:mm"
  studioId: number;
  requirePrepayment: boolean;
  isMultisportAvailable: boolean;
  timestamp: number;
}

const STORAGE_KEY = "pendingReservationIntent";
const INTENT_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Save a pending reservation intent before redirecting to login.
 */
export const savePendingReservationIntent = (
  intent: Omit<IPendingReservationIntent, "timestamp">
): void => {
  const intentWithTimestamp: IPendingReservationIntent = {
    ...intent,
    timestamp: Date.now(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(intentWithTimestamp));
};

/**
 * Retrieve the pending reservation intent if it exists and is not expired.
 * Returns null if no intent exists or if it has expired.
 * Automatically clears expired intents.
 */
export const getPendingReservationIntent =
  (): IPendingReservationIntent | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return null;
      }

      const intent: IPendingReservationIntent = JSON.parse(stored);

      // Check if intent has expired
      const now = Date.now();
      if (now - intent.timestamp > INTENT_EXPIRY_MS) {
        clearPendingReservationIntent();
        return null;
      }

      return intent;
    } catch {
      // Invalid JSON or other error - clear and return null
      clearPendingReservationIntent();
      return null;
    }
  };

/**
 * Clear any pending reservation intent.
 * Should be called after successful reservation or on explicit cancellation.
 */
export const clearPendingReservationIntent = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};
